/**
 * PR-V5 — text-run sub-pixel screen-size gating.
 *
 * # Why this exists
 *
 * On the 685k-entity П35Р fixture at zoom-extents, ~95% of MText
 * entities collapse to sub-pixel height. The default text path still
 * pays the full per-MText cost: atlas load + run renderer + per-glyph
 * quad allocation = potentially ~100k InstancedBufferGeometry on a
 * single zoom-extents — emission worthless, but cost full.
 *
 * The gate classifies a run by `runHeightWorld × cameraZoom` (= run
 * height in CSS pixels) and tells the caller which of three
 * representations to emit:
 *
 *   * `skip`   — < 0.5 px : invisible on the pixel grid → emit nothing
 *   * `dot`    — < 2  px  : tiny → represent as a single Points dot
 *   * `render` — ≥ 2  px  : full glyph quads / strokes
 *
 * Thresholds are deliberately AutoCAD-aligned and match the existing
 * `lodPolicy.LOD_THRESHOLDS_PX` cuts (skip / point / bbox-line) so the
 * text gate slots into the same bucketing convention without
 * diverging numbers.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1 — full fidelity at visible zoom
 *
 * The gate only kicks in when the run *cannot* be perceived on the
 * pixel grid. As soon as the user zooms in past the 2 px cutoff, the
 * full SDF / SHX renderer path runs unmodified — every glyph metric,
 * oblique, decoration, embedded block, kerning lands intact. This is
 * an emit-time optimization, not a fidelity reduction.
 *
 * # Hysteresis
 *
 * Plain thresholds at 0.5 / 2 px would pop-flicker on slow zoom because
 * frame-to-frame floating-point jitter pushes runs across the boundary.
 * The renderer host (Scene3DStage) builds text meshes on entity-feed
 * change (one-shot); the existing PR-J `lodTransitions` hysteresis
 * already protects the per-entity LOD bucket the renderer reads on
 * every camera frame, so the text gate inherits hysteresis transitively
 * via that classification path. Direct callers of this helper that
 * recompute every frame should layer the same 10% buffer.
 */

/** Three-way text-run classification driven by screen height. */
export type TextRunScreenBucket = "skip" | "dot" | "render";

/**
 * Pixel thresholds that delimit text run buckets. Exposed as a `const`
 * so callers (and unit tests) can introspect them without re-deriving
 * the numbers. Values intentionally match `lodPolicy.LOD_THRESHOLDS_PX`
 * so the text gate slots into the same screen-space cadence.
 */
export const TEXT_RUN_THRESHOLDS_PX = {
  /** Below this — drop the entity entirely (sub-pixel). */
  skip: 0.5,
  /** Below this — render as a single Points dot. */
  dot: 2,
} as const;

/**
 * Classify a single text run by its on-screen height.
 *
 *   * `runHeightPx < 0.5`        → `"skip"`
 *   * `0.5 ≤ runHeightPx < 2`    → `"dot"`
 *   * `runHeightPx ≥ 2`          → `"render"`
 *
 * Negative / zero zoom is non-physical; the helper clamps to 0 so the
 * classifier degenerates to `skip` instead of NaN.
 */
export function shouldSkipTextRun(
  runHeightWorld: number,
  cameraZoom: number,
): TextRunScreenBucket {
  if (!Number.isFinite(runHeightWorld) || runHeightWorld <= 0) return "skip";
  const z = Number.isFinite(cameraZoom) && cameraZoom > 0 ? cameraZoom : 0;
  const heightPx = runHeightWorld * z;
  if (heightPx < TEXT_RUN_THRESHOLDS_PX.skip) return "skip";
  if (heightPx < TEXT_RUN_THRESHOLDS_PX.dot) return "dot";
  return "render";
}

/**
 * Pixel-driven variant — useful when the caller already has the run
 * height in CSS pixels (e.g. reused from a previous classification).
 */
export function textRunBucketForPixels(
  heightPx: number,
): TextRunScreenBucket {
  if (!Number.isFinite(heightPx) || heightPx < TEXT_RUN_THRESHOLDS_PX.skip) {
    return "skip";
  }
  if (heightPx < TEXT_RUN_THRESHOLDS_PX.dot) return "dot";
  return "render";
}
